
import { useState } from 'react';

const faqs = [
  {
    category: 'Ingredients',
    questions: [
      {
        q: 'What ingredients are used in Daliah Secret products?',
        a: 'All Daliah Secret products are formulated with 100% natural ingredients sourced from trusted farms in Morocco, India, and France. Our perfumes use pure essential oils, botanical extracts, and natural fixatives. Our hair oils feature cold-pressed argan, jojoba, and rosemary oils blended with nourishing vitamins.',
      },
      {
        q: 'Are your products free from harmful chemicals?',
        a: 'Absolutely. Every product is free from parabens, sulfates, phthalates, synthetic fragrances, and artificial colorants. We never test on animals and hold cruelty-free certification. Our formulations are dermatologist-reviewed and safe for sensitive skin.',
      },
      {
        q: 'Do you use organic ingredients?',
        a: 'Yes, the majority of our ingredients are certified organic. We partner directly with small-scale organic farms to ensure the highest purity and traceability. Each ingredient batch is tested for quality before entering our production process.',
      },
      {
        q: 'Can I use your products if I have allergies?',
        a: 'We recommend reviewing the full ingredient list on each product page before purchasing. While our products are made with gentle, natural ingredients, some botanical extracts may cause reactions in individuals with specific plant allergies. If you have concerns, please consult your dermatologist or contact our support team.',
      },
    ],
  },
  {
    category: 'Shipping & Orders',
    questions: [
      {
        q: 'What are your shipping options and delivery times?',
        a: 'We offer standard shipping (5–7 business days) and express shipping (2–3 business days) across the UAE, GCC, Europe, and North America. Orders placed before 2 PM GST on business days are dispatched the same day. Free standard shipping is available on orders over $75.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes, we ship to over 40 countries worldwide. International delivery typically takes 7–14 business days depending on your location. Customs duties and import taxes, if applicable, are the responsibility of the recipient.',
      },
      {
        q: 'How can I track my order?',
        a: 'Once your order is dispatched, you\u2019ll receive a confirmation email with a tracking number and a link to follow your package in real time. You can also check your order status anytime by logging into your account on our website.',
      },
      {
        q: 'What is your return and exchange policy?',
        a: 'We accept returns and exchanges within 14 days of delivery for unopened, unused products in their original packaging. If a product arrives damaged, please contact us within 48 hours with photos and we\u2019ll arrange a free replacement or full refund.',
      },
    ],
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<string | null>('Ingredients-0');

  const toggle = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <section className="py-24 bg-[#FAFAF8]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#2E7D32] text-sm tracking-[0.2em] mb-3 font-medium">
            FREQUENTLY ASKED QUESTIONS
          </p>
          <h2 className="text-4xl font-serif text-gray-900 mb-4 leading-snug">
            Everything You Need to Know
          </h2>
          <p className="text-gray-500 text-[15px] max-w-lg mx-auto leading-relaxed">
            Find answers to common questions about our natural ingredients, 
            shipping policies, and more.
          </p>
        </div>

        <div className="space-y-10">
          {faqs.map((group) => (
            <div key={group.category}>
              <h3 className="text-xs font-semibold tracking-[0.15em] text-[#2E7D32] uppercase mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  {group.category === 'Ingredients' ? (
                    <i className="ri-leaf-line text-sm"></i>
                  ) : (
                    <i className="ri-truck-line text-sm"></i>
                  )}
                </span>
                {group.category}
              </h3>
              <div className="space-y-2">
                {group.questions.map((item, idx) => {
                  const key = `${group.category}-${idx}`;
                  const isOpen = openIndex === key;
                  return (
                    <div
                      key={key}
                      className={`rounded-xl border transition-all duration-300 ${
                        isOpen
                          ? 'border-[#2E7D32]/20 bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between px-6 py-4.5 text-left cursor-pointer group"
                        aria-expanded={isOpen}
                      >
                        <span
                          className={`text-[15px] font-medium pr-4 transition-colors ${
                            isOpen ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                          }`}
                        >
                          {item.q}
                        </span>
                        <span
                          className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                            isOpen
                              ? 'bg-[#2E7D32] text-white rotate-180'
                              : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                          }`}
                        >
                          <i className="ri-arrow-down-s-line text-base"></i>
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="px-6 pb-5 text-gray-500 text-[14px] leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-3">
            Still have questions?
          </p>
          <a
            href="mailto:hello@daliahsecret.com"
            className="inline-flex items-center gap-2 text-[#2E7D32] text-sm font-medium hover:underline cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-sm"></i>
            </span>
            Contact Our Team
          </a>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.flatMap((group) =>
              group.questions.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.a,
                },
              }))
            ),
          }),
        }}
      />
    </section>
  );
}
